import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Box, Card, CardActionArea, CardContent, Chip, Grid, Stack, Typography } from "@mui/material"
import TaskIcon from "@mui/icons-material/TaskAltOutlined"
import ForumIcon from "@mui/icons-material/ForumOutlined"
import BusinessIcon from "@mui/icons-material/BusinessOutlined"
import FolderIcon from "@mui/icons-material/FolderSpecialOutlined"

import { taskApi, chatApi, clientApi, projectApi } from "../api"
import { useAuth } from "../context/AuthContext"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import { LoadingRows } from "../components/LoadingState"
import { BRAND_FILL, PRIORITY_COLORS } from "../theme"
import { outlinedChipSx, shadeToward } from "../theme/contrast"
import { avatarColor, formatDate, initialsOf, isOverdue, relativeTime } from "../utils/format"
import { INTERACTIVE_CARD, rise } from "../motion"
import CountUp from "../components/CountUp"

/** The topic marker. Blue rather than the brand green, so it reads as a label on
 *  the row rather than as the row being selected. */
const TOPIC_COLOR = "#2f86e0"

/**
 * The landing screen.
 *
 * What it shows depends on what the person can reach — a client's Team Member and
 * a back-office Head of Operations should not get the same page. Everything is
 * fetched behind a permission check, and a panel the user cannot see is never
 * requested rather than requested and discarded.
 */
export default function DashboardPage() {
  const { user } = useAuth()
  const { can, isBackOffice, organizationLabel, roleLabel } = usePermissions()
  const navigate = useNavigate()

  const [myTasks, setMyTasks] = useState([])
  const [rooms, setRooms] = useState([])
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const requests = []

    if (can("tasks", "view")) {
      requests.push(
        taskApi
          .list({ mine: "true", open: "true", limit: 8 })
          .then((data) => setMyTasks(data.tasks || []))
          .catch(() => {}),
      )
    }
    if (can("chat", "view")) {
      requests.push(
        chatApi
          .rooms()
          .then((data) => setRooms((data.rooms || []).slice(0, 6)))
          .catch(() => {}),
      )
    }
    if (can("clients", "view")) {
      requests.push(
        clientApi
          .list()
          .then((data) => setClients(data.organizations || []))
          .catch(() => {}),
      )
    }
    if (can("projects", "view")) {
      requests.push(
        projectApi
          .list({ limit: 6 })
          .then((data) => setProjects(data.projects || []))
          .catch(() => {}),
      )
    }

    Promise.all(requests).finally(() => setLoading(false))
  }, [can])

  const unreadTotal = rooms.reduce((sum, room) => sum + (room.unread_count || 0), 0)
  const activeClients = clients.filter((c) => c.id !== "0" && c.status === "active").length

  const hour = new Date().getHours()
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening"

  return (
    <Box>
      <PageHeader
        title={`${greeting}, ${(user?.name || user?.email || "").split(" ")[0] || "there"}`}
        subtitle={<WhoseDashboard label={isBackOffice ? "TallyWise Back Office" : organizationLabel} role={roleLabel} isBackOffice={isBackOffice} />}
      />

      {loading ? (
        <LoadingRows rows={3} height={110} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {can("tasks", "view") ? (
              <Stat index={0} icon={TaskIcon} label="Open tasks assigned to you" value={myTasks.length} onClick={() => navigate("/tasks")} />
            ) : null}
            {can("chat", "view") ? (
              <Stat index={1} icon={ForumIcon} label="Unread messages" value={unreadTotal} onClick={() => navigate("/chat")} />
            ) : null}
            {can("clients", "view") ? (
              <Stat index={2} icon={BusinessIcon} label="Active clients" value={activeClients} onClick={() => navigate("/clients")} />
            ) : null}
            {can("projects", "view") ? (
              <Stat index={3} icon={FolderIcon} label="Projects" value={projects.length} onClick={() => navigate("/projects")} />
            ) : null}
          </Grid>

          <Grid container spacing={2}>
            {can("tasks", "view") ? (
              <Grid size={{ xs: 12, lg: 7 }}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h3" gutterBottom>
                      Your work
                    </Typography>
                    {myTasks.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nothing assigned to you right now.
                      </Typography>
                    ) : (
                      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
                        {myTasks.map((task, index) => (
                          <Box
                            key={task.id}
                            onClick={() => navigate("/tasks")}
                            sx={{ py: 1.25, cursor: "pointer", "&:hover": { bgcolor: "action.hover" }, ...rise(index) }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: PRIORITY_COLORS[task.priority], flexShrink: 0 }} />
                              <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", flexShrink: 0 }}>
                                {task.task_code}
                              </Typography>
                              <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                {task.title}
                              </Typography>
                              {task.due_date ? (
                                /* The word, not just the colour. A bare date in
                                   red says "something is wrong with this row" to
                                   anyone who reads red as an error — and says
                                   nothing at all to the one person in twelve who
                                   cannot separate it from the grey beside it. */
                                <Typography
                                  variant="caption"
                                  sx={{ flexShrink: 0 }}
                                  fontWeight={isOverdue(task.due_date) ? 600 : 400}
                                  color={isOverdue(task.due_date) ? "error.main" : "text.secondary"}
                                >
                                  {isOverdue(task.due_date) ? "Overdue · " : ""}
                                  {formatDate(task.due_date, "MMM D")}
                                </Typography>
                              ) : null}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ) : null}

            {can("chat", "view") ? (
              <Grid size={{ xs: 12, lg: 5 }}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h3" gutterBottom>
                      Recent conversations
                    </Typography>
                    {rooms.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No conversations yet.
                      </Typography>
                    ) : (
                      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
                        {rooms.map((room, index) => (
                          <Box
                            key={room.id}
                            onClick={() => navigate(`/chat/${room.id}`)}
                            sx={{ py: 1.25, cursor: "pointer", "&:hover": { bgcolor: "action.hover" }, ...rise(index) }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              {/* A name on this list could be a person or a
                                  thread, and the two are read differently — one
                                  is somebody waiting on you, the other is a
                                  subject. The pill is the only thing that says
                                  which, since a topic is named after its
                                  subject and a client's topic reads exactly
                                  like a company. */}
                              {room.kind !== "dm" ? (
                                <Chip
                                  size="small"
                                  label="Topic"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 10, flexShrink: 0, ...outlinedChipSx(TOPIC_COLOR) }}
                                />
                              ) : null}
                              <Typography variant="body2" fontWeight={room.unread_count ? 700 : 500} noWrap sx={{ flex: 1 }}>
                                {room.kind === "dm" ? room.peer?.name || "Direct message" : room.topic || room.title}
                              </Typography>
                              {room.unread_count > 0 ? (
                                <Chip size="small" color="error" label={room.unread_count} sx={{ height: 18, fontSize: 10 }} />
                              ) : null}
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {relativeTime(room.last_message_at || room.created_at)}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ) : null}
          </Grid>
        </>
      )}
    </Box>
  )
}

function Stat({ icon: Icon, label, value, onClick, index = 0 }) {
  return (
    <Grid size={{ xs: 6, md: 3 }}>
      <Card sx={{ height: "100%", ...INTERACTIVE_CARD, ...rise(index) }}>
        <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ color: "primary.dark" }}>
                <Icon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h1" sx={{ lineHeight: 1 }}>
                  <CountUp value={value} />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  )
}

/**
 * Whose dashboard this is.
 *
 * ── Why the business gets a mark ────────────────────────────────────────────
 *
 * This was one line of grey body text, the same size and weight as a caption on
 * a card below it — so the single most important fact on the screen, whose data
 * you are looking at, was also the quietest thing on it. For anybody who works
 * across several clients that is the line that decides whether the numbers
 * underneath mean anything.
 *
 * The monogram is derived from the name rather than stored, so every client has
 * one from the moment they are created and none of them needs a logo uploaded
 * before the screen looks finished.
 */
function WhoseDashboard({ label, role, isBackOffice }) {
  if (!label) return null
  const initials = initialsOf(label)
  /* The brand step that carries white, not the brand itself: the monogram is
     white on this square, and white on #7ed348 is 1.86:1. */
  const tint = isBackOffice ? BRAND_FILL : avatarColor(label)

  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        aria-hidden
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: "grid",
          placeItems: "center",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.02em",
          flexShrink: 0,
          color: "#fff",
          // A gradient rather than a flat fill: at 34px a flat square reads as a
          // placeholder, and the second stop is what makes it look made rather
          // than defaulted.
          background: `linear-gradient(135deg, ${tint} 0%, ${shadeToward(tint, "#10230a", 0.35)} 100%)`,
          boxShadow: `0 2px 8px -2px ${tint}80`,
        }}
      >
        {initials}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body1" fontWeight={650} noWrap sx={{ lineHeight: 1.25, letterSpacing: "-0.01em" }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
          <Chip
            size="small"
            label={isBackOffice ? "Back office" : "Client"}
            sx={{ height: 18, fontSize: 10, fontWeight: 600, ...outlinedChipSx(isBackOffice ? "#4d8f22" : TOPIC_COLOR) }}
          />
          {role ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {role}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  )
}
